import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography,
  Alert
} from '@mui/material';
import { 
  Email as EmailIcon,
  ArrowBack
} from '@mui/icons-material';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import '../assets/css/futuristic-modern.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setError('');
    
    try {
      const response = await axios.post(`${API_BASE_URL}/api/forgot-password`, { email });
      setMessage(response.data.message);
      setEmail('');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={24}
          sx={{
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(20px)',
            padding: { xs: 4, md: 5 },
            position: 'relative'
          }}
        >
          {/* Back Button */}
          <Button
            component={Link}
            to="/signin"
            startIcon={<ArrowBack />}
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              color: 'var(--text-muted)',
              '&:hover': {
                color: 'var(--neon-cyan)'
              }
            }}
          >
            Back
          </Button>

          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
            <Box
              sx={{
                width: 70,
                height: 70,
                margin: '0 auto',
                mb: 2,
                borderRadius: '50%',
                background: 'var(--accent-gradient)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <EmailIcon sx={{ fontSize: 35, color: 'white' }} />
            </Box>
            
            <Typography 
              variant="h4" 
              component="h1" 
              sx={{ 
                color: 'var(--text-primary)',
                fontWeight: 600,
                mb: 1
              }}
            >
              Forgot Password?
            </Typography>
            
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-muted)',
                lineHeight: 1.6
              }}
            >
              No worries! Enter your email and we'll send you a reset link.
            </Typography>
          </Box>

          {/* Success Message */}
          {message && (
            <Alert 
              severity="success"
              sx={{ 
                mb: 3,
                background: 'rgba(70, 130, 48, 0.1)',
                border: '1px solid rgba(70, 130, 48, 0.3)',
                color: '#468230'
              }}
            >
              {message}
            </Alert>
          )}

          {/* Error Message */}
          {error && (
            <Alert 
              severity="error"
              sx={{ 
                mb: 3,
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                color: '#ff6b6b'
              }}
            >
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoFocus
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--surface)',
                  '& fieldset': {
                    borderColor: 'var(--border)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'var(--neon-cyan)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'var(--text-muted)',
                  '&.Mui-focused': {
                    color: 'var(--neon-cyan)',
                  },
                },
              }}
            />

            <Button
              type="submit"
              fullWidth
              size="large"
              disabled={loading}
              className="btn-modern-primary"
              sx={{
                padding: '14px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 'var(--radius-md)',
                background: 'var(--accent-gradient)',
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a2666, #7a3896)',
                  boxShadow: 'none'
                },
                '&:disabled': {
                  opacity: 0.6
                }
              }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </Box>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
              Remember your password?{' '}
              <Link 
                to="/signin" 
                style={{ 
                  color: 'var(--neon-cyan)', 
                  textDecoration: 'none',
                  fontWeight: 600
                }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ForgotPassword;

